import { useState, useEffect, useCallback } from 'react';
import { apiFetch, apiPut, apiPost } from '../api/client';
import { PageContainer } from '../components/PageContainer';

interface ScoringStatus {
  pendingCount: number;
  unscoredCount: number;
  scoringCount: number;
  estimatedCostUsd: number;
  batchCap: number;
  threshold: number;
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: '640px' },
  heading: { fontSize: '20px', fontWeight: 700, color: '#e8eaff', marginBottom: '2rem' },
  section: {
    background: '#111118',
    border: '1px solid #222230',
    borderRadius: '10px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  sectionTitle: { fontSize: '13px', fontWeight: 600, color: '#a0a8ff', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  label: { fontSize: '13px', color: '#888' },
  value: { fontSize: '13px', color: '#e8eaff', fontWeight: 500 },
  costValue: { fontSize: '13px', color: '#7defa0', fontWeight: 600 },
  input: {
    background: '#0a0a12',
    border: '1px solid #222230',
    borderRadius: '6px',
    padding: '5px 10px',
    fontSize: '13px',
    color: '#e8eaff',
    width: '80px',
    textAlign: 'right' as const,
  },
  buttonRow: { display: 'flex', gap: '0.75rem', marginTop: '1rem' },
  primaryButton: {
    padding: '8px 20px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: '#a0a8ff',
    color: '#0a0a12',
    transition: 'opacity 0.15s',
  },
  secondaryButton: {
    padding: '8px 20px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid #222230',
    background: 'transparent',
    color: '#888',
    transition: 'opacity 0.15s',
  },
  dangerButton: {
    padding: '8px 20px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: '#ff6b6b',
    color: '#fff',
    transition: 'opacity 0.15s',
  },
  notice: { fontSize: '13px', color: '#888', fontStyle: 'italic' },
  successBadge: {
    fontSize: '12px',
    color: '#7defa0',
    background: '#0d2b1a',
    border: '1px solid #1a5c35',
    borderRadius: '4px',
    padding: '2px 8px',
  },
  errorText: { fontSize: '13px', color: '#ff6b6b', marginTop: '0.5rem' },
  divider: { borderColor: '#222230', margin: '1rem 0' },
};

export default function ScoringPage() {
  const [status, setStatus] = useState<ScoringStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [batchCap, setBatchCap] = useState<number | ''>('');
  const [threshold, setThreshold] = useState<number | ''>('');
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<{ jobId: string; batchSize: number } | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiFetch<ScoringStatus>('/scoring/status');
      setStatus(data);
      setBatchCap(data.batchCap);
      setThreshold(data.threshold);
      setLoadError(null);
    } catch (err) {
      setLoadError('Could not load scoring status.');
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  async function saveSettings() {
    setSettingsError(null);
    setSettingsSaved(false);
    try {
      await apiPut('/settings/scoring', { batchCap: Number(batchCap), threshold: Number(threshold) });
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
      fetchStatus();
    } catch {
      setSettingsError('Failed to save settings.');
    }
  }

  async function runScoring() {
    setRunning(true);
    setRunError(null);
    setRunResult(null);
    try {
      const data = await apiPost<{ jobId: string; batchSize: number }>('/scoring/run', {});
      setRunResult({ jobId: data.jobId, batchSize: data.batchSize });
      setConfirming(false);
      fetchStatus();
    } catch {
      setRunError('Failed to enqueue scoring run.');
    } finally {
      setRunning(false);
    }
  }

  return (
    <PageContainer>
    <div style={styles.page}>
      <h1 style={styles.heading}>Aesthetic Scoring</h1>

      {loadError && <p style={styles.errorText}>{loadError}</p>}

      {status && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Queue Status</div>
          <div style={styles.row}>
            <span style={styles.label}>Pending candidates</span>
            <span style={styles.value}>{status.pendingCount.toLocaleString()}</span>
          </div>
          <div style={styles.row}>
            <span style={styles.label}>Unscored</span>
            <span style={styles.value}>{status.unscoredCount.toLocaleString()}</span>
          </div>
          <hr style={styles.divider} />
          <div style={styles.row}>
            <span style={styles.label}>Will score in next run</span>
            <span style={styles.value}>{status.scoringCount.toLocaleString()} images</span>
          </div>
          <div style={styles.row}>
            <span style={styles.label}>Estimated cost</span>
            <span style={styles.costValue}>${status.estimatedCostUsd.toFixed(4)}</span>
          </div>
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Settings</div>
        <div style={styles.row}>
          <span style={styles.label}>Batch cap</span>
          <input
            style={styles.input}
            type="number"
            min={1}
            max={10000}
            value={batchCap}
            onChange={(e) => setBatchCap(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Score threshold (0–100)</span>
          <input
            style={styles.input}
            type="number"
            min={0}
            max={100}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </div>
        <div style={{ fontSize: '12px', color: '#555', marginBottom: '1rem' }}>
          Threshold is used to surface high-scoring candidates first in the review queue. It does not automatically reject images.
        </div>
        <div style={styles.buttonRow}>
          <button style={styles.primaryButton} onClick={saveSettings}>Save Settings</button>
          {settingsSaved && <span style={styles.successBadge}>Saved</span>}
        </div>
        {settingsError && <p style={styles.errorText}>{settingsError}</p>}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Run Haiku Scoring</div>

        {status?.unscoredCount === 0 ? (
          <p style={styles.notice}>No unscored candidates available.</p>
        ) : !confirming && !runResult ? (
          <div>
            <p style={{ fontSize: '13px', color: '#888', marginBottom: '1rem' }}>
              This will score up to <strong style={{ color: '#e8eaff' }}>{status?.scoringCount.toLocaleString()}</strong> candidates
              using Claude Haiku. Estimated cost:{' '}
              <strong style={{ color: '#7defa0' }}>${status?.estimatedCostUsd.toFixed(4)}</strong>.
            </p>
            <div style={styles.buttonRow}>
              <button style={styles.primaryButton} onClick={() => setConfirming(true)}>
                Run Scoring
              </button>
            </div>
          </div>
        ) : confirming ? (
          <div>
            <p style={{ fontSize: '13px', color: '#e8eaff', marginBottom: '1rem' }}>
              Confirm: score <strong>{status?.scoringCount.toLocaleString()}</strong> images for approximately{' '}
              <strong style={{ color: '#7defa0' }}>${status?.estimatedCostUsd.toFixed(4)}</strong>?
            </p>
            <div style={styles.buttonRow}>
              <button style={styles.dangerButton} onClick={runScoring} disabled={running}>
                {running ? 'Enqueueing…' : 'Confirm & Run'}
              </button>
              <button style={styles.secondaryButton} onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </div>
            {runError && <p style={styles.errorText}>{runError}</p>}
          </div>
        ) : runResult ? (
          <div>
            <p style={{ fontSize: '13px', color: '#7defa0', marginBottom: '0.5rem' }}>
              ✓ Scoring job enqueued — {runResult.batchSize.toLocaleString()} images queued (job {runResult.jobId})
            </p>
            <button style={styles.secondaryButton} onClick={() => setRunResult(null)}>
              Run Another Batch
            </button>
          </div>
        ) : null}
      </div>
    </div>
    </PageContainer>
  );
}